import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { paymentsApi } from '../services/api';
import { PaymentRequest, PaymentSubmitRequest } from '../types/customer';

interface PaymentState {
  payments: PaymentRequest[];
  currentPayment: PaymentRequest | null;
  loading: boolean;
  error: string | null;
  submissionSuccess: boolean;
}

const initialState: PaymentState = {
  payments: [],
  currentPayment: null,
  loading: false,
  error: null,
  submissionSuccess: false,
};

interface GenericError {
  message: string;
  errors?: Record<string, string[]>;
}

// Async thunk to fetch user payment requests
export const fetchUserPayments = createAsyncThunk<
  PaymentRequest[],
  void,
  { rejectValue: GenericError }
>('payment/fetchUserPayments', async (_, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.getMyPayments();
    return response.payments;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Failed to fetch payments' });
  }
});

// Async thunk to fetch single payment by id
export const fetchPaymentById = createAsyncThunk<
  PaymentRequest,
  string,
  { rejectValue: GenericError }
>('payment/fetchPaymentById', async (paymentId, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.getPaymentById(paymentId);
    return response.payment;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Failed to fetch payment details' });
  }
});

// Async thunk to submit new payment request
export const submitPaymentRequest = createAsyncThunk<
  string,
  PaymentSubmitRequest,
  { rejectValue: GenericError }
>('payment/submitPaymentRequest', async (paymentData, { rejectWithValue }) => {
  try {
    const response = await paymentsApi.submitPayment(paymentData);
    return response.message;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Payment submission failed' });
  }
});

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    resetSubmissionSuccess(state) {
      state.submissionSuccess = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUserPayments
      .addCase(fetchUserPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPayments.fulfilled, (state, action: PayloadAction<PaymentRequest[]>) => {
        state.loading = false;
        state.payments = action.payload;
      })
      .addCase(fetchUserPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch payments';
      })

      // fetchPaymentById
      .addCase(fetchPaymentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action: PayloadAction<PaymentRequest>) => {
        state.loading = false;
        state.currentPayment = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch payment details';
      })

       .addCase(submitPaymentRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.submissionSuccess = false;
      })
      .addCase(submitPaymentRequest.fulfilled, (state) => {
        state.loading = false;
        state.submissionSuccess = true;
      })
      .addCase(submitPaymentRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Payment submission failed';
        state.submissionSuccess = false;
      });
  },
});

export const { resetSubmissionSuccess, clearError } = paymentSlice.actions;

export default paymentSlice.reducer;
